import {useEffect, useState} from 'react'
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaExclamationCircle,
  FaFileAlt,
  FaFileCsv,
  FaFileExcel,
  FaTimes,
} from 'react-icons/fa'
import {DownloadItem, downloadStore} from './downloadStore'
import './DownloadTray.css'

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, exponent)
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`
}

const formatElapsed = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const FileIcon = ({item}: {item: DownloadItem}) => {
  if (item.status === 'done') return <FaCheckCircle className='dl-tray__icon dl-tray__icon--done' />
  if (item.status === 'error')
    return <FaExclamationCircle className='dl-tray__icon dl-tray__icon--error' />

  const name = item.fileName.toLowerCase()
  const className = `dl-tray__icon${item.status === 'cancelled' ? ' dl-tray__icon--cancelled' : ''}`
  if (name.endsWith('.csv')) return <FaFileCsv className={className} />
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return <FaFileExcel className={className} />
  return <FaFileAlt className={className} />
}

const DismissButton = ({id}: {id: string}) => (
  <button
    type='button'
    className='dl-tray__action'
    onClick={() => downloadStore.remove(id)}
    title='Dismiss'
  >
    <FaTimes />
  </button>
)

const ItemRow = ({item, now}: {item: DownloadItem; now: number}) => {
  const isDownloading = item.status === 'downloading'
  const hasTotal = item.total > 0
  const percent = hasTotal ? Math.min(100, Math.round((item.loaded / item.total) * 100)) : 0
  const elapsed = formatElapsed((item.finishedAt ?? now) - item.startedAt)

  let fillClass = 'dl-tray__bar-fill'
  if (item.status === 'done') fillClass += ' dl-tray__bar-fill--done'
  else if (isDownloading && !hasTotal) fillClass += ' dl-tray__bar-fill--indeterminate'

  // An indeterminate bar is positioned by the marquee animation, not by width.
  const isIndeterminate = isDownloading && !hasTotal
  const fillWidth = isIndeterminate ? undefined : `${isDownloading ? percent : 100}%`

  return (
    <div className='dl-tray__item' data-cy='download-tray-item'>
      <FileIcon item={item} />
      <div className='dl-tray__body'>
        <div className='dl-tray__name' title={item.fileName}>
          {item.fileName}
        </div>

        {item.status !== 'error' && (
          <div className='dl-tray__bar'>
            <div className={fillClass} style={fillWidth ? {width: fillWidth} : undefined} />
          </div>
        )}

        <div className={`dl-tray__meta${item.status === 'error' ? ' dl-tray__meta--error' : ''}`}>
          {isDownloading && (
            <>
              <span>
                {`Downloading… ${formatBytes(item.loaded)}`}
                {hasTotal ? ` of ${formatBytes(item.total)} (${percent}%)` : ''}
              </span>
              <span>{elapsed}</span>
              <span className='dl-tray__spacer' />
              <button
                type='button'
                className='dl-tray__action'
                onClick={item.cancel}
                title='Cancel download'
                data-cy='download-tray-cancel'
              >
                <FaTimes />
              </button>
            </>
          )}

          {item.status === 'done' && (
            <>
              <span>{`Completed · ${formatBytes(item.loaded)}`}</span>
              <span>{elapsed}</span>
              <span className='dl-tray__spacer' />
              <DismissButton id={item.id} />
            </>
          )}

          {item.status === 'cancelled' && (
            <>
              <span>Cancelled</span>
              <span className='dl-tray__spacer' />
              <DismissButton id={item.id} />
            </>
          )}

          {item.status === 'error' && (
            <>
              <span>{item.errorMessage}</span>
              <span className='dl-tray__spacer' />
              <DismissButton id={item.id} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Chrome-style download tray. Mounted once globally (src/index.tsx) so an export keeps
 * reporting progress even after the user navigates away from the table that started it,
 * and so several concurrent exports each get their own row.
 *
 * Renders nothing when there is nothing to show.
 */
const DownloadTray = () => {
  const [items, setItems] = useState<DownloadItem[]>(downloadStore.getItems())
  const [collapsed, setCollapsed] = useState(false)
  const [now, setNow] = useState(Date.now())

  useEffect(() => downloadStore.subscribe(setItems), [])

  const activeCount = items.filter((item) => item.status === 'downloading').length

  // Drives the elapsed-time counters; only ticks while something is in flight.
  useEffect(() => {
    if (!activeCount) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(timer)
  }, [activeCount])

  if (!items.length) return null

  const toggle = () => setCollapsed((value) => !value)

  return (
    <div className='dl-tray' data-cy='download-tray'>
      <div
        className='dl-tray__header'
        onClick={toggle}
        role='button'
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') toggle()
        }}
      >
        <span className='dl-tray__title'>
          {activeCount
            ? `Downloading ${activeCount} file${activeCount > 1 ? 's' : ''}…`
            : 'Downloads'}
        </span>
        <button
          type='button'
          className='dl-tray__header-btn'
          title={collapsed ? 'Expand' : 'Collapse'}
          onClick={(event) => {
            event.stopPropagation()
            toggle()
          }}
        >
          {collapsed ? <FaChevronUp /> : <FaChevronDown />}
        </button>
        <button
          type='button'
          className='dl-tray__header-btn'
          title={activeCount ? 'Cancel and close' : 'Close'}
          data-cy='download-tray-close'
          onClick={(event) => {
            event.stopPropagation()
            downloadStore.closeAll()
          }}
        >
          <FaTimes />
        </button>
      </div>

      {!collapsed && (
        <div className='dl-tray__list'>
          {items.map((item) => (
            <ItemRow key={item.id} item={item} now={now} />
          ))}
        </div>
      )}
    </div>
  )
}

export default DownloadTray
